import {
    Account,
    BalanceSheetCategory,
    IncomeStatementCategory,
} from "@/entities/DataAssociation/Account.model";
import clsx from "clsx";
import { useMemo } from "react";
import { getAccounts } from "@/entities/DataAssociation/DataAssociation.queries";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { ACCOUNT_LIST } from "../../constants/labeling.constants";
import { AccountList } from "../AccountList";
import {
    AccountHintType,
    DraftAccount,
} from "@/entities/DataAssociation/Draft.model";
import { useRowSelection } from "../../hooks/useRowSelection";
import { useNewAccounts } from "../../hooks/useNewAccounts";

export function ExistingAccountList({
    hint,
    selectedCategory,
}: {
    hint?: DraftAccount;
    selectedCategory: IncomeStatementCategory | BalanceSheetCategory;
}) {
    const newAccounts = useNewAccounts();
    const { onAccountSelection } = useRowSelection();
    const labels = Object.values(ACCOUNT_LIST).filter(
        (account) => account.category === selectedCategory,
    );

    const { data: accounts } = useApiQuery(getAccounts, {
        params: { labels: labels.map((label) => label.code) },
    });

    const sortedAccounts = useMemo(() => {
        const allAccounts = [
            ...Object.values(newAccounts || {})
                .map((row) => row.account)
                .filter((account) => !!account)
                .map(
                    (account) =>
                        ({
                            id: account.accountId,
                            accountName: account.accountName,
                            label: account.label,
                            companyId: "",
                            labelSource: "",
                            ignore: false,
                            isoCurrencyCode: "",
                            createdBy: "",
                            createdAt: "",
                            identifiers: [],
                        }) as Account,
                ),
            ...(accounts || []),
        ];
        return allAccounts.sort((a, b) => {
            if (hint?.type === AccountHintType.EXISTING) {
                if (a.id === hint.accountId) return -1;
                if (b.id === hint.accountId) return 1;
            }
            const canPrioritize =
                !!hint &&
                ACCOUNT_LIST[hint.label]?.category === selectedCategory;
            if (canPrioritize) {
                if (a.label === hint!.label) return -1;
                if (b.label === hint!.label) return 1;
            }
            return a.accountName.localeCompare(b.accountName);
        });
    }, [accounts, hint, selectedCategory]);

    if (!sortedAccounts) return null;
    return (
        <div className={clsx("flex flex-col pb-8")}>
            <AccountList
                accounts={sortedAccounts}
                recommendedIds={
                    hint?.type === AccountHintType.EXISTING && hint.accountId
                        ? [hint.accountId]
                        : []
                }
                onAccountSelection={(account) =>
                    onAccountSelection({
                        type: AccountHintType.EXISTING,
                        accountId: account.id,
                        accountName: account.accountName,
                        label: account.label,
                    })
                }
            />
        </div>
    );
}
